// The "What to Expect" booklet, one entry per step of the form. Edit the copy
// here and the page follows: steps, progress and what ops reads back.
//
// The couple ticks each section once (SectionAck in WhatToExpect.js), and that
// tick is saved against every topic in it, so ops still reads the booklet
// back topic by topic.
//
// A part carries `title`, `label` (the step rail), an optional `intro` and its
// `topics`. A topic carries any of, rendered in this order:
//   intro      plain paragraph
//   note       shaded aside under the intro
//   body       paragraph, for a topic with no lists
//   points     bulleted list under the body
//   expect / notExpect   the two columns
//   faqs       question and answer pairs
//   footnote   shaded line at the end
// `intro` on a part, and `intro` / `body` on a topic, may be an array to give
// several paragraphs.
//
// `reelShooter: 'none' | 'basic' | 'premium' | 'both'` shows a topic only on a
// booking whose package has that reel shooter, or both kinds. Variants of the
// same topic share a key, so ops reads them as one row.
//
// `introLink: { label, url }` puts a link at the end of the intro, or
// `introLinks: [{ label, url }]` several on the line under it, and
// `footnoteLinks: [{ label, url }]` puts links at the end of the footnote.

// `{brand}` in any copy is the platform's name, EventGraphia or WedGraphia,
// set by each app through configureWeddingDay. Without one it is dropped.

// The first page, before any section. Nothing on it is ticked.
export const COVER = {
  // The platform's name goes in front where the host gives one.
  title: 'What to Expect',
  subtitle: 'Your guide to coverage, deliverables and the details that matter',
  body: 'This booklet explains how we cover your wedding, what you receive, and the practical things worth knowing before your day. Please read it carefully. Much of what you get depends on the time you plan for and the conditions of your day.',
};

export const PARTS = [
  {
    key: 'coverage',
    label: 'Your coverage',
    title: 'How your day is covered',
    topics: [
      {
        key: 'references',
        title: 'Your references and moodboards',
        intro: 'You can share references for photos, videos and reels. They show our team the direction you love, and we use them to shape our approach on the day.',
        expect: [
          'Your references guide how our team approaches your coverage. We work towards giving ourselves the best chance of capturing something close to them',
          'Your moodboard picks guide your portrait sessions: your couple and solo shoots, and how our team approaches them',
          'The first 10 to 15 minutes of any portrait session are a warm-up. It’s when you get comfortable and our team works out the approach that suits you best',
          'The more time our team has after that, the more they can actively try out creative ideas inspired by your references',
        ],
        notExpect: [
          'An exact match of any reference. How your photos and films look depends on your venue, light, setup and the time of your events',
          'Moodboards deciding how your events look. Each event sets its own mood: a night Sangeet naturally reads dark and dramatic, an outdoor Haldi in daylight reads bright and vibrant',
          'Sunset light shots at a night event',
        ],
      },
      {
        key: 'priority',
        title: 'Your coverage',
        intro: 'Our team covers your day in a set order of priority: what’s happening first, then the couple and your expressions, then your family.',
        expect: [
          'Whatever is happening at that moment is captured first, whether it’s your engagement, ceremony, varmala or any ritual',
          'Around it, the focus is on the two of you and your expressions',
          'Your family is covered next',
          'Your guests are captured as your events unfold, following the flow of the day',
        ],
        notExpect: [
          'Everyone captured at every moment. Our team follows the priority order above',
          'More than one angle of a moment when only one photographer and videographer are covering it',
          'A photograph of every guest',
        ],
      },
      {
        key: 'requests',
        title: 'Specific photo requests',
        expect: [
          'We consider any specific photograph you ask for',
        ],
        notExpect: [
          'A guarantee. Whether it happens depends on the time available, your décor and theme, and whether the people in it are there when we are',
        ],
      },
    ],
  },
  {
    key: 'planning',
    label: 'Your planning',
    title: 'The planning that’s in your hands',
    topics: [
      {
        key: 'portraits',
        title: 'Portrait session',
        intro: 'Around an hour is ideal, thirty minutes the minimum. The first 10 to 15 minutes of any session go into getting you comfortable and capturing the standard shots. Creative, reference driven shots come after that.',
        expect: [
          'A full creative session when you plan around an hour',
        ],
        notExpect: [
          'Creative shots in a short session. With less time, you get the standard shots only',
        ],
      },
      {
        key: 'interviews',
        title: 'Interviews',
        intro: 'Interviews are light hearted, relaxed conversations, not formal or scripted. They need a slot in your schedule, and that slot is yours to plan. Our team can’t create the time on the day.',
        expect: [
          'The bride and groom are interviewed separately, each after getting ready on the wedding day',
          'Parents, siblings and friends are interviewed on Day 1, as planned in the schedule',
          'Interviews used in your wedding film, where they add to the story, and all interviews delivered separately after your video delivery on request',
        ],
        notExpect: [
          'A joint couple interview (unless planned)',
          'Interviews that weren’t planned into your schedule',
          'Every interview included in your Wedding Film. Which ones make the film is the team’s call',
        ],
      },
      {
        key: 'family',
        title: 'Family photographs',
        intro: 'Family photographs are with your immediate family: your parents and siblings.',
        expect: [
          'Family photographs in the slot you plan for them. Set aside 30 minutes, 10 to 15 at the very least',
        ],
        notExpect: [
          'Family photographs without a planned slot',
          'Photographs with someone in particular unless you plan time for it, so they’re there and so are you',
        ],
      },
    ],
  },
  {
    key: 'deliverables',
    label: 'What you receive',
    title: 'What you receive',
    topics: [
      {
        key: 'photos',
        title: 'Photographs',
        expect: [
          'A full gallery with every usable photo from your day',
          'Multiple variations of the same shot, where available, so you have options to pick from',
          'An Editor’s Choice with the best moments in one refined view',
        ],
        notExpect: [
          'One single edited version of each moment. We give you all the usable variations to choose from',
          'Blurred or technically poor images. These are the only ones left out',
        ],
      },
      {
        key: 'video-note',
        title: 'A note on all video deliverables',
        body: [
          'All your video deliverables are built from the same coverage. We do not shoot separately for any of them. On the day, capturing your events is the priority. On the edit table, we make the best of what was captured, and which moments make the final cut is our team’s call.',
          'Your music preferences matter. Your playlists are how our editors understand your taste, and the music in your films is chosen to match it. We don’t guarantee that the exact songs you share are used, because the edit has to serve the footage we capture. A wedding film also carries every kind of music, emotional, party, dance and instrumental, because your events call for it.',
        ],
      },
      {
        key: 'highlight',
        title: 'Wedding highlight',
        intro: 'A short, cinematic edit of around three to five minutes. The trailer for your wedding film.',
        expect: [
          'The major, key moments that define your day, woven into one story',
          'A polished, cinematic piece around three to five minutes long',
          'Music chosen to match the taste you shared',
        ],
        notExpect: [
          'The exact songs you shared',
          'A specific list of moments you want included',
          'Every moment of your day',
        ],
      },
      {
        key: 'film',
        title: 'Wedding film',
        intro: 'A sequential flow video that captures all the events across your wedding celebrations, in clear order.',
        expect: [
          'All your events, captured in the order they happened',
          'A clear, sequential record of your celebrations',
          'Interviews included where they add to the story, at the team’s discretion',
          'Music chosen to match the taste you shared',
        ],
        notExpect: [
          'A short, cinematic edit like the highlight. The film is the full sequential record, not a condensed version',
          'Every interview included in full',
          'The exact songs you shared. The final call is the team’s, based on the event flow and the visuals we have',
        ],
      },
      {
        key: 'reels',
        reelShooter: 'none',
        title: 'Reels',
        intro: 'Short vertical edits built from the landscape event coverage we already capture of your day.',
        introLink: {
          label: 'View Example Reels',
          url: 'https://www.youtube.com/playlist?list=PLLTYleqOOaG4',
        },
        expect: [
          'Short vertical edits built from the coverage of your events',
          'Footage shot in landscape and cropped to vertical. Nothing is shot vertically',
          'Music chosen to match the taste you shared, with the final call made by the team based on the visuals',
        ],
        notExpect: [
          'Reels shot separately from your event coverage',
          'Dedicated behind the scenes content',
          'Trending format reels that need to be specifically shot',
          'An exact track',
        ],
        footnote: 'Want reels made your way? You can add a dedicated reel shooter at an extra cost, so the reels you have in mind can be shot and created for vertical.',
        footnoteLinks: [
          { label: 'Premium Reel Shooter', url: 'https://www.youtube.com/playlist?list=PLE9aFm1Ka8Lw' },
          { label: 'Basic Reel Shooter', url: 'https://www.youtube.com/playlist?list=PLFTSrfDbR6dg' },
        ],
      },
      {
        key: 'reels',
        reelShooter: 'premium',
        title: 'Reels',
        intro: 'Your package includes a dedicated reel shooter, who films specifically for short vertical content alongside your main coverage.',
        introLink: {
          label: 'View Example Reels',
          url: 'https://www.youtube.com/playlist?list=PLE9aFm1Ka8Lw',
        },
        expect: [
          'Reels shot for vertical by your reel shooter, in the styles you picked in your preferences',
          'Music chosen to match the taste you shared, with the final call made by the team based on the visuals',
        ],
        notExpect: [
          'An exact recreation of a specific reel you’ve seen',
          'An exact track',
        ],
      },
      {
        key: 'reels',
        reelShooter: 'basic',
        title: 'Reels',
        intro: 'Your package includes a dedicated reel shooter, who films specifically for short vertical content alongside your main coverage.',
        introLink: {
          label: 'View Example Reels',
          url: 'https://www.youtube.com/playlist?list=PLFTSrfDbR6dg',
        },
        expect: [
          'Reels shot for vertical by your reel shooter, in the styles you picked in your preferences',
          'Music chosen to match the taste you shared, with the final call made by the team based on the visuals',
        ],
        notExpect: [
          'An exact recreation of a specific reel you’ve seen',
          'An exact track',
        ],
      },
      {
        key: 'reels',
        reelShooter: 'both',
        title: 'Reels',
        intro: 'Your package includes a dedicated reel shooter, who films specifically for short vertical content alongside your main coverage.',
        introLinks: [
          { label: 'Premium Reel Shooter', url: 'https://www.youtube.com/playlist?list=PLE9aFm1Ka8Lw' },
          { label: 'Basic Reel Shooter', url: 'https://www.youtube.com/playlist?list=PLFTSrfDbR6dg' },
        ],
        expect: [
          'Reels shot for vertical by your reel shooter, in the styles you picked in your preferences',
          'Music chosen to match the taste you shared, with the final call made by the team based on the visuals',
        ],
        notExpect: [
          'An exact recreation of a specific reel you’ve seen',
          'An exact track',
        ],
      },
      {
        key: 'performance',
        title: 'Performance cut',
        intro: 'A raw, sequential compilation of all the performances from your event. If performances happen across more than one day, '
          + 'you get a separate cut for each day. Each performance is captured on two cameras, one on a tripod and one moving, '
          + 'and mixed into a single flow.',
        expect: [
          'All your performances compiled in the order they happened',
          'Two camera coverage, a tripod and a moving camera, mixed into one cut',
          'A separate cut for each day performances take place',
          'Audio exactly as recorded on camera',
        ],
        notExpect: [
          'A polished or cinematic edit. This is a raw compilation cut',
          'Enhanced, re-mixed or studio audio',
          'Added music by default. If you share your performance music, we can include it',
          'A separate cut of each performance',
        ],
      },
    ],
  },
  {
    key: 'good-to-know',
    label: 'Good to know',
    title: 'Good to know',
    topics: [
      {
        key: 'timelines',
        title: 'Delivery timelines and revisions',
        body: 'Your delivery timelines and the number of revision rounds are set out in your proposal. They differ from package to package, so please check yours.',
      },
      {
        key: 'forms',
        title: 'Changing your forms',
        body: 'Once you submit your Creative Preferences and Wedding Day Details forms, they lock. If anything changes after that, get in touch with your {brand} coordinator.',
      },
      {
        key: 'rain',
        title: 'Rain',
        body: 'We cannot shoot while it is raining. Our equipment is not waterproof.',
      },
      {
        key: 'lasers',
        title: 'Lasers',
        body: 'Lasers can damage camera sensors, so our team will not point cameras directly at them.',
      },
      {
        key: 'drone',
        title: 'Drone',
        body: 'Drone coverage depends on the package you have booked. If it is included, it will be there. Flying a drone always needs permission, and government zone rules apply:',
        points: [
          'Green zone: written permission from the venue, arranged by you',
          'Yellow zone: written permission from both the venue and the local police station, arranged by you. Police permission can take time, so confirm your venue’s zone early',
          'Red zone: a no fly zone. A drone will not be operated',
        ],
      },
      {
        key: 'permissions',
        title: 'Other permissions',
        body: 'Some locations and venues need permissions or permits for photography. Arranging these is your responsibility, and without them we cannot shoot in those areas.',
      },
      {
        key: 'delays',
        title: 'Delayed schedule',
        body: 'We cover your events as they happen. Delays come out of your portrait and creative time, not your rituals, so running late means fewer of those shots.',
      },
      {
        key: 'team-room',
        title: 'Team room at the venue',
        body: 'Where your package includes accommodation for our team, at least one room must be on the event premises. Our team stores and secures your data, charges equipment and takes backups from that room through the day. An off-site hotel doesn’t work for this.',
      },
    ],
  },
];

// The booklet this booking is shown: every topic, less the variants written
// for the other kind of package.
// Every string in the copy with `{brand}` filled in, or dropped with the
// space after it where there is no brand.
const withBrand = (value, brand) => {
  if (typeof value === 'string') {
    return brand ? value.split('{brand}').join(brand) : value.split('{brand} ').join('');
  }
  if (Array.isArray(value)) return value.map((item) => withBrand(item, brand));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, withBrand(item, brand)]));
  }
  return value;
};

export const partsFor = ({ reelShooter = 'none', brand = '' }) =>
  withBrand(PARTS.map((part) => ({
    ...part,
    topics: part.topics.filter(
      (topic) => topic.reelShooter === undefined || topic.reelShooter === reelShooter),
  })), brand);
